import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form, Badge, Table, Alert, Dropdown } from "react-bootstrap";
import { 
  FaPlus, 
  FaPhone, 
  FaComments, 
  FaBug, 
  FaLightbulb, 
  FaHeadset,
  FaExclamationTriangle,
  FaRegClock,
  FaCheckCircle,
  FaSearch,
  FaCalendarAlt,
  FaEdit,
  FaTrash
} from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const TicketingSystem = () => {
  const [tickets, setTickets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  const [newTicket, setNewTicket] = useState({
    type: "Bug",
    subject: "",
    description: "",
    priority: "Low",
    requestCall: false,
    developer: ""
  });

  // Get customer ID from token
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCustomerId(decoded.id);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  // Fetch tickets for the customer
  useEffect(() => {
    if (customerId) {
      fetchTickets();
    }
  }, [customerId]);

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`http://localhost:5890/Customertiketdetails/${customerId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const formattedTickets = response.data.map(ticket => ({
        id: ticket._id,
        type: ticket.type,
        subject: ticket.subject,
        description: ticket.description,
        status: ticket.status || "Open",
        priority: ticket.priority,
        requestCall: ticket.requestCall,
        date: new Date(ticket.createdAt).toLocaleString(),
        assigned: ticket.developer,
        progress: ticket.progress || 0
      }));
      
      setTickets(formattedTickets);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setError("Failed to load tickets. Please try again later.");
      setIsLoading(false);
    }
  };

  const handleAddTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      setError("Please fill all required fields");
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.post("http://localhost:5890/add-ticket", {
        customerId,
        type: newTicket.type,
        priority: newTicket.priority,
        developer: newTicket.developer,
        subject: newTicket.subject,
        description: newTicket.description,
        requestCall: newTicket.requestCall
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const addedTicket = {
        id: response.data._id,
        type: response.data.type,
        subject: response.data.subject,
        description: response.data.description,
        status: "Open",
        priority: response.data.priority,
        requestCall: response.data.requestCall,
        date: new Date(response.data.createdAt).toLocaleString(),
        assigned: response.data.developer,
        progress: 0
      };

      setTickets([...tickets, addedTicket]);
      setShowModal(false);
      setNewTicket({
        type: "Bug",
        subject: "",
        description: "",
        priority: "Low",
        requestCall: false,
        developer: ""
      });
      setError(null);
      setSuccess("Ticket created successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error adding ticket:", error);
      setError(error.response?.data?.error || "Failed to create ticket. Please try again.");
    }
  };

  const handleEditTicket = (ticket) => {
    setCurrentTicket(ticket);
    setNewTicket({
      type: ticket.type,
      subject: ticket.subject,
      description: ticket.description,
      priority: ticket.priority,
      requestCall: ticket.requestCall,
      developer: ticket.assigned
    });
    setShowEditModal(true);
  };

  const handleUpdateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      setError("Please fill all required fields");
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.put(
        `http://localhost:5890/update-ticket/${currentTicket.id}`,
        {
          type: newTicket.type,
          priority: newTicket.priority,
          developer: newTicket.developer,
          subject: newTicket.subject,
          description: newTicket.description,
          requestCall: newTicket.requestCall
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const updatedTickets = tickets.map(ticket => 
        ticket.id === currentTicket.id ? {
          ...ticket,
          type: newTicket.type,
          subject: newTicket.subject,
          description: newTicket.description,
          priority: newTicket.priority,
          requestCall: newTicket.requestCall,
          assigned: newTicket.developer
        } : ticket
      );

      setTickets(updatedTickets);
      setShowEditModal(false);
      setCurrentTicket(null);
      setError(null);
      setSuccess("Ticket updated successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error updating ticket:", error);
      setError(error.response?.data?.error || "Failed to update ticket. Please try again.");
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) {
      return;
    }

    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`http://localhost:5890/delete-ticket/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const filteredTickets = tickets.filter(ticket => ticket.id !== ticketId);
      setTickets(filteredTickets);
      setSuccess("Ticket deleted successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error deleting ticket:", error);
      setError(error.response?.data?.error || "Failed to delete ticket. Please try again.");
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "All" || ticket.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case "Open":
        return <Badge bg="warning" className="d-flex align-items-center gap-1"><FaRegClock /> Open</Badge>;
      case "In Progress":
        return <Badge bg="primary" className="d-flex align-items-center gap-1"><FaRegClock /> In Progress</Badge>;
      case "Closed":
        return <Badge bg="success" className="d-flex align-items-center gap-1"><FaCheckCircle /> Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case "High":
        return <Badge bg="danger" className="d-flex align-items-center gap-1"><FaExclamationTriangle /> High</Badge>;
      case "Medium":
        return <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1">Medium</Badge>;
      case "Low":
        return <Badge bg="success" className="d-flex align-items-center gap-1">Low</Badge>;
      default:
        return <Badge bg="secondary">{priority}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case "Bug":
        return <FaBug className="text-danger" />;
      case "Feature":
        return <FaLightbulb className="text-warning" />;
      case "Support":
        return <FaHeadset className="text-primary" />;
      default:
        return null;
    }
  };

  const getActionButton = (action) => {
    return action ? (
      <Button variant="outline-warning" size="sm" className="d-flex align-items-center gap-1">
        <FaPhone /> Call
      </Button>
    ) : (
      <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-1">
        <FaComments /> Chat
      </Button>
    );
  };

  const stats = {
    open: tickets.filter(t => t.status === "Open").length,
    inProgress: tickets.filter(t => t.status === "In Progress").length,
    closed: tickets.filter(t => t.status === "Closed").length,
    highPriority: tickets.filter(t => t.priority === "High").length
  };

  if (isLoading) {
    return (
      <div className="container my-4 p-4 bg-white rounded-3 shadow-sm text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="container my-4 p-4 bg-white rounded-3 shadow-sm">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">
            <FaHeadset className="me-2 text-primary" />
            Ticketing System
          </h3>
          <p className="text-muted mb-0">Manage and track all support requests</p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)} className="d-flex align-items-center gap-2">
          <FaPlus /> Create Ticket
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        <div className="col-md-3">
          <div className="card border-start border-primary border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Open Tickets</h6>
                  <h3 className="mb-0">{stats.open}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <FaRegClock className="text-primary" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-start border-info border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">In Progress</h6>
                  <h3 className="mb-0">{stats.inProgress}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <FaRegClock className="text-info" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-start border-success border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Closed</h6>
                  <h3 className="mb-0">{stats.closed}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaCheckCircle className="text-success" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-start border-danger border-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">High Priority</h6>
                  <h3 className="mb-0">{stats.highPriority}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded">
                  <FaExclamationTriangle className="text-danger" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4 g-3">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4">
          <Form.Select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="d-flex align-items-center gap-2"
          >
            <option value="All">All Statuses</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Closed">Closed</option>
          </Form.Select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="table-responsive rounded-3 border">
        <Table hover className="mb-0">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Subject</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Date</th>
              <th>Action</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.length > 0 ? (
              filteredTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td className="fw-semibold">#{ticket.id.substring(0, 6)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {getTypeIcon(ticket.type)}
                      {ticket.type}
                    </div>
                  </td>
                  <td>
                    <div className="fw-semibold">{ticket.subject}</div>
                    <small className="text-muted">{ticket.description.substring(0, 50)}...</small>
                  </td>
                  <td>{ticket.assigned || "Unassigned"}</td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td>{getPriorityBadge(ticket.priority)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      <FaCalendarAlt className="text-muted" size={12} />
                      <small>{new Date(ticket.date).toLocaleDateString()}</small>
                    </div>
                  </td>
                  <td>{getActionButton(ticket.requestCall)}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="light" size="sm" className="rounded-circle">
                        <FiMoreVertical />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleEditTicket(ticket)}>
                          <FaEdit className="me-2" /> Edit
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDeleteTicket(ticket.id)} className="text-danger">
                          <FaTrash className="me-2" /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  {searchTerm || filterStatus !== "All" ? (
                    "No tickets match your search criteria"
                  ) : (
                    "No tickets found"
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Add Ticket Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaPlus /> Create New Ticket
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="ticketType" className="mb-3">
                  <Form.Label>Ticket Type</Form.Label>
                  <Form.Select
                    value={newTicket.type}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, type: e.target.value })
                    }
                  >
                    <option value="Bug"><FaBug className="me-2" /> Bug</option>
                    <option value="Feature"><FaLightbulb className="me-2" /> Feature</option>
                    <option value="Support"><FaHeadset className="me-2" /> Support</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="priority" className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Low"
                      name="priority"
                      id="priorityLow"
                      value="Low"
                      checked={newTicket.priority === "Low"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Medium"
                      name="priority"
                      id="priorityMedium"
                      value="Medium"
                      checked={newTicket.priority === "Medium"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="High"
                      name="priority"
                      id="priorityHigh"
                      value="High"
                      checked={newTicket.priority === "High"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="developer" className="mb-3">
                  <Form.Label>Assign Developer</Form.Label>
                  <Form.Select
                    value={newTicket.developer}
                    onChange={(e) => setNewTicket({ ...newTicket, developer: e.target.value })}
                  >
                    <option value="">Not assigned</option>
                    <option value="John Doe">John Doe</option>
                    <option value="Jane Smith">Jane Smith</option>
                    <option value="Mike Johnson">Mike Johnson</option>
                    <option value="Sarah Williams">Sarah Williams</option>
                    <option value="David Brown">David Brown</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="subject" className="mb-3">
              <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={newTicket.subject}
                placeholder="Briefly describe the issue"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, subject: e.target.value })
                }
                required
                isInvalid={!newTicket.subject}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a subject
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="description" className="mb-3">
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={newTicket.description}
                placeholder="Provide detailed information about the ticket"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, description: e.target.value })
                }
                required
                isInvalid={!newTicket.description}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a description
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="requestCall"
              label="Request immediate callback for this issue"
              checked={newTicket.requestCall}
              onChange={(e) =>
                setNewTicket({
                  ...newTicket,
                  requestCall: e.target.checked,
                })
              }
              className="mb-3"
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTicket}
            disabled={!newTicket.subject || !newTicket.description}
          >
            Submit Ticket
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Ticket Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaEdit /> Edit Ticket
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="editTicketType" className="mb-3">
                  <Form.Label>Ticket Type</Form.Label>
                  <Form.Select
                    value={newTicket.type}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, type: e.target.value })
                    }
                  >
                    <option value="Bug"><FaBug className="me-2" /> Bug</option>
                    <option value="Feature"><FaLightbulb className="me-2" /> Feature</option>
                    <option value="Support"><FaHeadset className="me-2" /> Support</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="editPriority" className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Low"
                      name="editPriority"
                      id="editPriorityLow"
                      value="Low"
                      checked={newTicket.priority === "Low"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Medium"
                      name="editPriority"
                      id="editPriorityMedium"
                      value="Medium"
                      checked={newTicket.priority === "Medium"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="High"
                      name="editPriority"
                      id="editPriorityHigh"
                      value="High"
                      checked={newTicket.priority === "High"}
                      onChange={(e) =>
                        setNewTicket({ ...newTicket, priority: e.target.value })
                      }
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group controlId="editDeveloper" className="mb-3">
                  <Form.Label>Assign Developer</Form.Label>
                  <Form.Select
                    value={newTicket.developer}
                    onChange={(e) => setNewTicket({ ...newTicket, developer: e.target.value })}
                  >
                    <option value="">Not assigned</option>
                    <option value="John Doe">John Doe</option>
                    <option value="Jane Smith">Jane Smith</option>
                    <option value="Mike Johnson">Mike Johnson</option>
                    <option value="Sarah Williams">Sarah Williams</option>
                    <option value="David Brown">David Brown</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="editSubject" className="mb-3">
              <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={newTicket.subject}
                placeholder="Briefly describe the issue"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, subject: e.target.value })
                }
                required
                isInvalid={!newTicket.subject}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a subject
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="editDescription" className="mb-3">
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={newTicket.description}
                placeholder="Provide detailed information about the ticket"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, description: e.target.value })
                }
                required
                isInvalid={!newTicket.description}
              />
              <Form.Control.Feedback type="invalid">
                Please enter a description
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="editRequestCall"
              label="Request immediate callback for this issue"
              checked={newTicket.requestCall}
              onChange={(e) =>
                setNewTicket({
                  ...newTicket,
                  requestCall: e.target.checked,
                })
              }
              className="mb-3"
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateTicket}
            disabled={!newTicket.subject || !newTicket.description}
          >
            Update Ticket
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TicketingSystem;